/**
 * Un seul cadeau par rang : avant d'en ajouter un, on retire celui qui était déjà là, et on
 * refuse tout second choix tant que le premier n'a pas abouti.
 *
 * Tout le reste est natif — `<product-form>` fait l'ajout Ajax et le re-rendu des sections,
 * l'accordéon est en CSS, le volet s'ouvre par `aria-controls`. Ce composant n'existe que
 * pour cet écart-là, plus la fermeture animée du volet : sans elle, le re-rendu du tiroir
 * escamoterait le panneau d'un coup.
 */

class AxCartGift extends HTMLElement {
  #busy = false;
  #resubmit = null;

  connectedCallback() {
    this.addEventListener('submit', this.#onSubmit, { capture: true });

    if (this.dataset.threshold) this.#applyThreshold();
  }

  /**
   * Hors euros, le tiroir rend la ligne masquée avec le seuil en centimes d'euro et le montant payé
   * dans la devise du client : seul le navigateur connaît le taux de Shopify pour les comparer.
   * Shopify arrondit les prix convertis (un 100 ml à 225 € s'affiche 365 CAD, quand 225 × taux
   * donne 365,003) : 1 % de tolérance évite de refuser le cadeau pour un arrondi.
   *
   * Panier insuffisant : la ligne reste masquée, et le cadeau déjà choisi part par le même retrait
   * que celui que le Liquid commande en euros.
   */
  #applyThreshold() {
    const rate = Number(window.Shopify?.currency?.rate) || 1;
    const minimum = Number(this.dataset.threshold) * rate * 0.99;

    if (Number(this.dataset.paid) >= minimum) {
      this.hidden = false;
      return;
    }

    const key = this.dataset.chosenKey;
    if (!key) return;

    const cleanup = document.createElement('ax-cart-cleanup');
    cleanup.dataset.removeKey = key;
    this.append(cleanup);
  }

  #onSubmit = (event) => {
    const form = event.target;

    // Notre propre relance, une fois l'ancien cadeau retiré : on laisse passer au natif.
    if (this.#resubmit === form) {
      this.#resubmit = null;
      return;
    }

    this.querySelector('.ax-cart-gift__panel')?.hide();

    // Le tiroir se re-rend après l'ajout et remplace ce composant : le verrou meurt avec lui.
    if (this.#busy) return this.#stop(event);
    this.#busy = true;

    const key = this.dataset.chosenKey;
    if (!key) return; // rien à retirer : `<product-form>` prend la main

    // On suspend l'envoi le temps du retrait, puis on rejoue la soumission à l'identique.
    this.#stop(event);
    const submitter = event.submitter;
    delete this.dataset.chosenKey;

    fetch(`${Shopify.routes.root}cart/change.js`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest' // sans quoi Shopify ne contrôle pas le stock
      },
      body: JSON.stringify({ id: key, quantity: 0 })
    }).then(() => {
      this.#resubmit = form;

      // `requestSubmit` n'existe que depuis Safari 16 ; le clic du bouton produit le même
      // événement, avec le même `submitter`.
      if (form.requestSubmit) {
        form.requestSubmit(submitter);
      } else {
        submitter.click();
      }
    });
  };

  /** `stopPropagation` en phase de capture : l'événement n'atteint pas `<product-form>`. */
  #stop(event) {
    event.preventDefault();
    event.stopPropagation();
  }
}

if (!window.customElements.get('ax-cart-gift')) {
  window.customElements.define('ax-cart-gift', AxCartGift);
}

/**
 * Filet de sécurité : le tiroir désigne une ligne qui n'a rien à faire au panier — un cadeau dont
 * le panier n'ouvre plus le droit, ou un produit à zéro euro ajouté depuis sa fiche. Sans ce
 * retrait, le client arrive au paiement avec un panier à zéro et Shopify refuse la commande.
 *
 * Les retraits sont sérialisés PAR CLÉ de ligne, et chacun vérifie que la ligne est encore là
 * avant d'agir. Deux rendus se suivent après un ajout — celui du tiroir, puis celui de
 * `cart:refresh` — et sans cette file le second marqueur rejouait un retrait déjà fait, que
 * Shopify refuse en 400.
 *
 * Un verrou qui se contenterait de bloquer serait faux : la clé de ligne n'est pas unique dans le
 * temps, Shopify redonne la même au même produit sans propriétés. Bloquer sans rejouer ferait
 * ignorer le deuxième ajout du même échantillon.
 *
 * Rien ne boucle : une fois la ligne retirée, le tiroir ne rend plus de marqueur, et un échec ne
 * déclenche pas de nouveau rendu.
 */
class AxCartCleanup extends HTMLElement {
  static #queue = new Map();

  connectedCallback() {
    const key = this.dataset.removeKey;
    if (!key) return;

    const precedent = AxCartCleanup.#queue.get(key) || Promise.resolve();

    const courant = precedent
      .then(() => fetch(`${Shopify.routes.root}cart.js`).then((response) => response.json()))
      .then((cart) => {
        if (!cart.items.some((item) => item.key === key)) return;

        return fetch(`${Shopify.routes.root}cart/change.js`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-Requested-With': 'XMLHttpRequest'
          },
          body: JSON.stringify({ id: key, quantity: 0 })
        }).then(() => document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true })));
      })
      .catch(() => {})
      .finally(() => {
        if (AxCartCleanup.#queue.get(key) === courant) AxCartCleanup.#queue.delete(key);
      });

    AxCartCleanup.#queue.set(key, courant);
  }
}

if (!window.customElements.get('ax-cart-cleanup')) {
  window.customElements.define('ax-cart-cleanup', AxCartCleanup);
}

/**
 * Pendant d'`AxCartCleanup` : le tiroir désigne les échantillons dus qui manquent au panier, on les
 * ajoute en un seul appel avec la property qui les rend gratuits. Les ajouts sont sérialisés et
 * chacun relit le panier avant d'agir : les deux rendus qui suivent un ajout poseraient sinon deux
 * marqueurs, donc deux exemplaires. Un échantillon que Regios a mis entre-temps vaut le nôtre.
 *
 * Rien ne boucle : une fois les lignes ajoutées, le tiroir ne rend plus de marqueur, et un refus
 * (rupture de stock) ne déclenche pas de nouveau rendu.
 */
class AxCartSampleAdd extends HTMLElement {
  static #queue = Promise.resolve();

  connectedCallback() {
    const variantIds = (this.dataset.variantIds || '').split(',').map(Number).filter(Boolean);
    const gift = this.dataset.gift;
    if (!variantIds.length || !gift) return;

    AxCartSampleAdd.#queue = AxCartSampleAdd.#queue
      .then(() => fetch(`${Shopify.routes.root}cart.js`).then((response) => response.json()))
      .then((cart) => {
        const missing = variantIds.filter((variantId) => !cart.items.some((item) =>
          item.variant_id === variantId &&
          (item.properties?._ax_gift === gift || item.properties?._regios_auto_added_by_discount_id)
        ));
        if (!missing.length) return;

        return fetch(`${Shopify.routes.root}cart/add.js`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-Requested-With': 'XMLHttpRequest'
          },
          body: JSON.stringify({ items: missing.map((id) => ({ id, quantity: 1, properties: { _ax_gift: gift } })) })
        }).then((response) => {
          if (response.ok) document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
        });
      })
      .catch(() => {});
  }
}

if (!window.customElements.get('ax-cart-sample-add')) {
  window.customElements.define('ax-cart-sample-add', AxCartSampleAdd);
}

/**
 * Le volet de sélection s'ouvre du côté, comme les autres tiroirs du site.
 *
 * `DrawerPopover` du thème n'anime que l'axe vertical : il remonte du bas, ce qui convient à
 * la note de commande — un panneau bas de quelques lignes — mais pas ici où le volet recouvre
 * tout le tiroir. On étend la classe plutôt que de sortir le volet du tiroir : on garde ainsi
 * le piège de focus, la fermeture par Échap et la restitution du focus.
 *
 * La classe est récupérée dans le registre : ce fichier est un script classique, il ne peut
 * pas importer le module du thème. `whenDefined` évite toute course au chargement.
 */
window.customElements.whenDefined('x-drawer-popover').then(() => {
  const DrawerPopover = window.customElements.get('x-drawer-popover');

  /**
   * Objet de contrôle minimal attendu par `DialogElement` : il l'utilise comme un thenable,
   * et appelle `cancel()` quand une transition en écrase une autre, `complete()` quand
   * l'animation est désactivée.
   */
  const controls = (animations) => {
    const finished = Promise.all(animations.map((animation) => animation.finished)).catch(() => {});

    return {
      then: (onFulfilled) => finished.then(onFulfilled),
      cancel: () => animations.forEach((animation) => animation.cancel()),
      complete: () => animations.forEach((animation) => animation.finish())
    };
  };

  class AxCartGiftPanel extends DrawerPopover {
    #slide(opening) {
      const content = this.getShadowPartByName('content');
      const offsets = opening ? ['translateX(100%)', 'translateX(0)'] : ['translateX(0)', 'translateX(100%)'];

      return controls([
        content.animate(
          { transform: offsets },
          { duration: 450, easing: 'cubic-bezier(0.86, 0, 0.07, 1)', fill: 'forwards' }
        )
      ]);
    }

    createEnterAnimationControls() {
      return this.#slide(true);
    }

    createLeaveAnimationControls() {
      return this.#slide(false);
    }
  }

  if (!window.customElements.get('ax-cart-gift-panel')) {
    window.customElements.define('ax-cart-gift-panel', AxCartGiftPanel);
  }
});
